import { supabase } from '@/lib/supabase';
import { toDashboardUiError } from '@/lib/dashboard-errors';
import type {
  ToolAppDefinition,
  ToolAppExecuteRequest,
  ToolAppExecuteResponse,
  ToolAppSurfaceDefinition,
  ToolAppViewBinding,
} from '@shared/types/tool-apps';
import type { AnyViewEnvelope, ViewDefinition } from '@shared/types/views';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isEnvelopeLike = (value: unknown): value is AnyViewEnvelope => (
  isRecord(value)
  && typeof value.view_type === 'string'
  && typeof value.schema_version === 'string'
  && isRecord(value.data)
);

const isViewDefinitionLike = (value: unknown): value is ViewDefinition => (
  isRecord(value)
  && typeof value.patternKey === 'string'
  && typeof value.schemaVersion === 'string'
  && isRecord(value.displayMeta)
  && isRecord(value.interactionPolicy)
  && isRecord(value.config)
  && value.fallbackPatternKey === 'fallback_empty'
);

const isViewBindingLike = (value: unknown): value is ToolAppViewBinding => (
  isRecord(value)
  && typeof value.id === 'string'
  && typeof value.order === 'number'
  && (value.width === 'full' || value.width === 'half' || value.width === 'third')
  && isViewDefinitionLike(value.viewDefinition)
);

const isFilterLike = (value: unknown): boolean => (
  isRecord(value)
  && typeof value.key === 'string'
  && typeof value.label === 'string'
  && (value.kind === 'text' || value.kind === 'select')
  && (value.operator === 'equals' || value.operator === 'contains')
  && typeof value.fieldKey === 'string'
  && Array.isArray(value.targetBindingIds)
  && value.targetBindingIds.every((entry) => typeof entry === 'string')
  && (
    value.options === undefined
    || (
      Array.isArray(value.options)
      && value.options.every((option) => (
        isRecord(option)
        && typeof option.value === 'string'
        && typeof option.label === 'string'
      ))
    )
  )
);

const isSurfaceLike = (value: unknown): value is ToolAppSurfaceDefinition => (
  isRecord(value)
  && typeof value.key === 'string'
  && (value.kind === 'collection' || value.kind === 'aggregate')
  && typeof value.title === 'string'
  && (value.filters === undefined || (Array.isArray(value.filters) && value.filters.every(isFilterLike)))
  && Array.isArray(value.views)
  && value.views.every(isViewBindingLike)
);

const isDefinitionLike = (value: unknown): value is ToolAppDefinition => (
  isRecord(value)
  && value.schemaVersion === '0.1.0'
  && value.profile === 'data_lens'
  && typeof value.title === 'string'
  && typeof value.defaultSurfaceKey === 'string'
  && Array.isArray(value.surfaces)
  && value.surfaces.every(isSurfaceLike)
);

const isToolAppExecuteResponse = (value: unknown): value is ToolAppExecuteResponse => (
  isRecord(value)
  && isRecord(value.toolApp)
  && typeof value.toolApp.id === 'string'
  && typeof value.toolApp.key === 'string'
  && typeof value.toolApp.title === 'string'
  && typeof value.toolApp.version === 'number'
  && isDefinitionLike(value.definition)
  && isSurfaceLike(value.surface)
  && isRecord(value.viewEnvelopesByBindingId)
  && Object.values(value.viewEnvelopesByBindingId).every(
    (entry) => entry === null || isEnvelopeLike(entry),
  )
);

export const invokeToolAppExecute = async (
  request: ToolAppExecuteRequest,
): Promise<ToolAppExecuteResponse> => {
  const { data, error } = await supabase.functions.invoke('tool-app-execute', {
    body: request,
  });

  if (error) {
    throw await toDashboardUiError(error, 'Impossible d’afficher ce Tool App pour le moment.');
  }

  if (!isToolAppExecuteResponse(data)) {
    throw new Error('Réponse invalide de tool-app-execute.');
  }

  return data;
};
